import type { CoverageTimelineView } from '@/types/coverage-timeline';
import { resolveBlockEnd } from '../missions/_components/MissionCoverageTimeline';

/**
 * THE GANTT, COLLAPSED TO ONE LANE.
 *
 * The COP's right column opens at 320 px and floors at 288 (`copSidePanelWidth.ts`). A five-lane
 * chart drawn in that is not a Gantt, it is a smear - so at rest the COP answers the smaller
 * question the width can carry honestly: IS THE STATION MANNED, ACROSS THE WINDOW. The per-aircraft
 * lanes are still there, one press away, in the expanded chart over the map.
 *
 * This module is the collapse, and it is pure on purpose: the rule for what counts as covered is
 * the one thing here an operator would act on, so it is testable without rendering a COP.
 *
 * COVERED MEANS SOMEBODY IS ON STATION. Not "an aircraft is airborne", not "a rotation exists" -
 * `on_station` and nothing else. `transit`, `standby`, `charging` and `swap` are all real work and
 * none of them is a manned station, which is exactly the distinction #402 got wrong on the map (a
 * green route drawn for a covered station with nothing flying).
 *
 * WHERE AN OPEN BLOCK ENDS IS NOT DECIDED HERE. `resolveBlockEnd` is imported from the chart this
 * collapses, because the first version of this file answered it independently - running an open
 * ACTUAL `on_station` to the edge of the window - and that is the difference between "an aircraft
 * is on station now" and "this station is manned for the next four hours". The open block is the
 * live `mission_segments` row, present in essentially every payload where a station is manned, so
 * this was not an edge case: it painted over every handover transit gap the projection draws, and
 * over the whole self-swap gap on a rotation of one (#564) - the single number that configuration
 * exists to make visible. Both reviewers found it independently. One function, or they drift again.
 *
 * THE THIRD STATE IS "UNKNOWN", AND IT IS NOT "UNCOVERED". When nothing is projected, the server
 * sends no forecast blocks at all, and everything after NOW is unplanned rather than empty:
 * drawing it red would raise an alarm about a hole nobody has predicted. It gets its own kind,
 * and the readings above it decline to answer rather than guessing. Two flags say it, because
 * there are two ways to get there and only one of them is "no plan exists":
 * `forecastAvailable: false` when there is no standing forecast to project from (no relief can be
 * chosen, coverage was only just attached, the station is `pending_station`), and
 * `forecastDrawn: false` when one IS standing and projects to nothing anyway - above all the
 * settling window after a handover, which is every cycle of every healthy rotation (#704).
 *
 * ONE STRETCH IS EXEMPT FROM THAT, UNDER EITHER FLAG: a hole the chart can already SEE at the
 * NOW rule. The station being empty is a fact the lanes carry, not a forecast, so it is drawn
 * `gap` and `nextGap` names it - otherwise the block reads "not forecast" over an unmanned
 * station. Its LENGTH is still unknown, which is what `RibbonSegment.unplanned` is for.
 */

export interface RibbonSegment {
  startMs: number;
  endMs: number;
  /**
   * `covered` - somebody is on station. `gap` - nobody is, and the chart says so.
   * `unknown` - there is no forecast for this stretch, so nothing is claimed either way.
   *
   * `gap` does NOT imply the stretch is forecast: a hole already under way at the NOW rule is
   * drawn `gap` in an unplanned future, because the station being empty is observed. Read
   * `unplanned` for what may be claimed about it.
   */
  kind: 'covered' | 'gap' | 'unknown';
  /**
   * Is this segment entirely in the past?
   *
   * The window routinely carries up to two hours of lookback (`timelineDomain`), so without this
   * a hole that closed ninety minutes ago is drawn exactly like one opening in twenty minutes -
   * and on a coverage ribbon, red means act. Segments are SPLIT at `now` so the boundary is a real
   * edge rather than a colour judgement, which also gives the NOW rule something to line up with.
   */
  past: boolean;
  /**
   * Is this stretch time for which NO PLAN EXISTS?
   *
   * Separate from `kind`, and it has to be, because the two answer different questions (#704
   * review). `kind` is what to PAINT; this is what may be CLAIMED. They agree everywhere except
   * on the one segment that is both - a hole the chart can already see at the NOW rule, in a
   * future nothing has forecast. That one is drawn `gap`, because the station really is empty and
   * an operator must be told; but its LENGTH is not known, because the server said it projected
   * nothing, and a relief could launch five minutes from now.
   *
   * Marking it lets `nextGap` name it while `hasUnknown` and `ribbonCoveredFraction` still
   * decline - without this, exempting the segment also bought a four-hour duration and a percent-
   * manned figure out of a payload that supports neither, which is the same overclaim #704 was
   * filed about wearing the opposite colour.
   */
  unplanned: boolean;
}

interface Interval { startMs: number; endMs: number }

/**
 * Collapse every lane's `on_station` blocks into one track across `domain`.
 *
 * Returns segments that tile the domain exactly, in order, with no zero-width entries - so a
 * renderer can lay them out as percentage widths and trust that they sum to the window. An empty
 * result means the domain itself is not drawable, never "no coverage": a wholly uncovered window
 * is one `gap` segment, which is a thing worth seeing.
 */
export function ribbonSegments(
  view: CoverageTimelineView | null,
  domain: { start: number; end: number } | null,
): RibbonSegment[] {
  if (!view || !domain) return [];
  const { start, end } = domain;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  if (!Array.isArray(view.lanes) || !view.lanes.every((l) => Array.isArray(l?.blocks))) return [];

  const now = new Date(view.now).getTime();
  if (!Number.isFinite(now)) return [];

  const onStation: Interval[] = [];
  for (const lane of view.lanes) {
    for (const block of lane.blocks) {
      if (block.type !== 'on_station') continue;
      const blockStart = new Date(block.startsAt).getTime();
      // The chart's rule, not one of our own - see the module comment.
      const blockEnd = resolveBlockEnd(block, lane.blocks, now, end);
      if (!Number.isFinite(blockStart) || !Number.isFinite(blockEnd)) continue;
      const clippedStart = Math.max(blockStart, start);
      const clippedEnd = Math.min(blockEnd, end);
      if (clippedEnd <= clippedStart) continue;
      onStation.push({ startMs: clippedStart, endMs: clippedEnd });
    }
  }

  onStation.sort((a, b) => a.startMs - b.startMs);

  // Merge overlaps. TWO AIRCRAFT ON STATION AT ONCE IS STILL ONE COVERED STATION - during a
  // handover the relief and the outgoing aircraft overlap by design, and drawing that as anything
  // other than continuous coverage would put a seam in the ribbon at the exact moment the rotation
  // is working correctly.
  const merged: Interval[] = [];
  for (const interval of onStation) {
    const last = merged[merged.length - 1];
    if (last && interval.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, interval.endMs);
    } else {
      merged.push({ ...interval });
    }
  }

  const coarse: Array<{ startMs: number; endMs: number; covered: boolean }> = [];
  let cursor = start;
  for (const interval of merged) {
    if (interval.startMs > cursor) {
      coarse.push({ startMs: cursor, endMs: interval.startMs, covered: false });
    }
    coarse.push({ startMs: interval.startMs, endMs: interval.endMs, covered: true });
    cursor = interval.endMs;
  }
  if (cursor < end) coarse.push({ startMs: cursor, endMs: end, covered: false });

  // TWO FIELDS, AND THE HONEST ANSWER IS EITHER OF THEM (#704).
  //
  // `forecastAvailable` says a forecast row is STANDING; `forecastDrawn` says one actually made
  // it onto the chart. They disagree for the whole settling window after a handover - the row
  // that put the incumbent there is still standing, and #592 deliberately draws no pair off it.
  // Reading `forecastAvailable` alone, this ribbon painted that window four hours of RED over a
  // rotation that was working, and then named it as the next gap. Nothing planned is nothing
  // planned however it got that way.
  //
  // Both are optional on the wire for the rolling-deploy reason the phase fields are: an API that
  // predates either sends nothing, and `!== false` reads that as "assume it forecast", the
  // behaviour every deployment had before they existed.
  const unplannedFuture = view.forecastAvailable === false || view.forecastDrawn === false;

  // AN EMPTY STATION IS OBSERVED, NOT FORECAST, AND "UNKNOWN" MUST NOT SWALLOW IT (#704 review).
  //
  // `unplannedFuture` is a statement about the PLAN, and the first cut applied it to every
  // uncovered stretch after NOW - including the remainder of a hole that is already under way.
  // That silenced the one reading this ribbon exists for. Traced: a station whose last actual
  // block closed an hour ago draws `gap` up to the NOW rule and `unknown` after it, and `nextGap`
  // skips both - the past half because it ends exactly AT NOW (`endMs > fromMs` is false), the
  // future half because it is no longer a gap. The block then reads "Next gap: not forecast" over
  // an unmanned station, where before #704 it read `NOW`.
  //
  // The distinction is whether the chart can SEE the station at NOW. Mid-settling-window it can -
  // the incumbent's actual block is open and runs forward through the NOW rule - so the empty
  // stretch beyond it is genuinely unplanned and stays `unknown`, which is the whole #704 fix. An
  // uncovered NOW is the opposite: the emptiness is a fact the lanes already carry, and the
  // stretch that begins at the rule is its remainder.
  //
  // CLOSED AT THE RULE STILL COUNTS AS COVERED (`now <= endMs`, not `<`). An OPEN actual block -
  // an aircraft on station with no scheduled end - is resolved by `resolveBlockEnd` to exactly
  // NOW, because that is the last instant the lanes have evidence for. A half-open test would
  // read the most ordinary state on the board, one aircraft holding station right now, as an
  // empty station, and paint the unplanned future red again - the very thing #704 fixed.
  const coveredAtNow = merged.some((i) => i.startMs <= now && now <= i.endMs);

  // Split at NOW so past and future are separate segments rather than one bar the eye has to
  // divide by itself - and so an unplanned future can be marked without touching the record.
  const segments: RibbonSegment[] = [];
  const push = (startMs: number, endMs: number, covered: boolean, past: boolean) => {
    if (endMs <= startMs) return;
    // Only the stretch that BEGINS at the NOW rule is the observed hole's remainder; anything
    // after it is still unplanned, so a later stretch does not inherit the exemption.
    const observedGapNow = !coveredAtNow && startMs === now;
    // Uncovered, after the rule, with nothing forecast: no plan covers this time. True for the
    // exempted segment too - being drawn `gap` changes what it looks like, not what is known.
    const unplanned = !past && unplannedFuture && !covered;
    const kind: RibbonSegment['kind'] = covered
      ? 'covered'
      : (unplanned && !observedGapNow) ? 'unknown' : 'gap';
    segments.push({ startMs, endMs, kind, past, unplanned });
  };
  for (const seg of coarse) {
    if (now > seg.startMs && now < seg.endMs) {
      push(seg.startMs, now, seg.covered, true);
      push(now, seg.endMs, seg.covered, false);
    } else {
      push(seg.startMs, seg.endMs, seg.covered, seg.endMs <= now);
    }
  }
  return segments;
}

/**
 * The share of the drawn window that is manned, 0-1.
 *
 * DELIBERATELY NOT "ACHIEVED COVERAGE", and deliberately not a compliance figure.
 * `GET /api/coverage/:id/achieved` walks telemetry fixes and is the authority on what actually
 * happened; this is arithmetic over a chart that is part forecast, whose window is the CHART's
 * domain (up to 2 h of lookback plus the projection horizon) and NOT the coverage window the
 * target percentage is set against. The caller must never print it beside that target as though
 * the two were measured over the same interval.
 *
 * Null when any stretch is `unplanned` - which is NOT the same as `kind === 'unknown'`, and the
 * difference is the point: an observed hole at the NOW rule is drawn `gap` so the operator is
 * told the station is empty, and it is still time nobody has planned. A percentage over that is a
 * number with no meaning, and the honest answer is to decline rather than round it down to zero.
 */
export function ribbonCoveredFraction(segments: readonly RibbonSegment[]): number | null {
  if (segments.length === 0) return null;
  // `unplanned`, not `kind === 'unknown'`: an observed hole at the rule is drawn `gap` so the
  // operator is told the station is empty, but it is still unplanned time and a share of it is
  // still a number with no meaning. See `RibbonSegment.unplanned`.
  if (segments.some((s) => s.unplanned)) return null;
  const total = segments.reduce((sum, s) => sum + (s.endMs - s.startMs), 0);
  if (total <= 0) return null;
  const covered = segments.reduce((sum, s) => (s.kind === 'covered' ? sum + (s.endMs - s.startMs) : sum), 0);
  return covered / total;
}

/**
 * The first uncovered stretch that has not already finished, or null when none is left.
 *
 * This is the reading the ribbon exists to surface: an operator scanning the COP needs the next
 * hole, not a colour they have to measure by eye. A gap already under way is returned rather than
 * skipped - it is the most urgent one there is, and the caller has to say so in words rather than
 * printing a start time that has already gone.
 *
 * WHAT COMES BACK FOR A GAP UNDER WAY IS ITS REMAINDER, not the whole hole, because segments are
 * split at NOW. That is the operator's question: "the station is empty - for how much longer?"
 * The minutes already spent uncovered are history, they are drawn on the ribbon to the left of the
 * NOW rule, and adding them to the number would overstate what is left to wait.
 *
 * A hole that closed an hour ago is NOT returned - it is drawn, because it happened, but it is not
 * something to act on. Neither is an `unknown` stretch: nobody has forecast a hole there, and
 * calling unplanned time a gap would raise an alarm the chart does not support.
 */
export function nextGap(
  segments: readonly RibbonSegment[],
  fromMs: number,
): RibbonSegment | null {
  return segments.find((s) => s.kind === 'gap' && s.endMs > fromMs) ?? null;
}

/**
 * Is any part of the drawn window unplanned? The readings use it to decline rather than guess.
 *
 * Reads `unplanned` rather than `kind`, so the one segment that is drawn `gap` despite being
 * unplanned - an observed hole at the NOW rule - still counts. The question this answers has
 * always been the one in its name, and the exemption is about paint, not knowledge.
 */
export function hasUnknown(segments: readonly RibbonSegment[]): boolean {
  return segments.some((s) => s.unplanned);
}
